'use client'

import { X } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

interface ContactButtonProps {
  icon: string
  alt: string
  width: number
  height: number
  className: string
  onClick?: () => void
  children?: React.ReactNode
  additionalClasses?: string
  href: string
  target?: string
}

const ContactButton = ({
  icon,
  alt,
  width,
  height,
  className,
  onClick,
  children,
  additionalClasses = '',
  href,
  target,
}: ContactButtonProps) => (
  <Link
    onClick={(e) => {
      if (!href) {
        e.preventDefault()
      }
      onClick?.()
    }}
    href={href}
    className={`contact-button flex items-center justify-center rounded-full ${additionalClasses}`}
    rel={href ? 'noopener noreferrer' : undefined}
    target={target}
  >
    <Image
      src={icon}
      alt={alt}
      width={width}
      height={height}
      className={className}
    />
    {children}
    <style jsx>{`
      .contact-button {
        opacity: 0;
        transform: translateY(10px);
        animation: slideIn 0.2s ease forwards;
      }

      .contact-button:hover {
        transform: scale(1.05);
        transition: transform 0.2s;
      }

      .contact-button:active {
        transform: scale(0.95);
      }

      @keyframes slideIn {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>
  </Link>
)

export const ContactFab = () => {
  const [isExpand, setIsExpand] = useState(false)

  const toggleExpand = () => setIsExpand((prev) => !prev)

  const renderMainButton = () => (
    <button
      onClick={toggleExpand}
      className='main-button group h-12 w-12 rounded-full lg:h-15 lg:w-15'
    >
      <Image
        src='/icons/icon-float-fab.svg'
        alt='icon-float-fab'
        width={68}
        height={68}
        className='h-12 w-12 transition-transform duration-300 group-hover:scale-110 lg:h-17 lg:w-17'
      />
      <style jsx>{`
        .main-button {
          opacity: 0;
          transform: scale(0.8);
          animation: scaleIn 0.12s ease forwards;
        }

        @keyframes scaleIn {
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </button>
  )

  const renderExpandedButtons = () => (
    <div className='expanded-buttons flex flex-col space-y-3'>
      <ContactButton
        icon='/icons/icon-zalo.svg'
        alt='icon-zalo'
        width={64}
        height={64}
        className='h-10 w-10 lg:h-15 lg:w-15'
        additionalClasses='shadow-sm'
        href='https://zalo.me/1517736583279228381'
        target='_blank'
      />

      <ContactButton
        icon='/icons/icon-phone.svg'
        alt='icon-phone'
        width={26}
        height={26}
        className='h-5 w-5 lg:h-6 lg:w-6'
        additionalClasses='group relative h-10 w-10 bg-primary shadow-sm lg:h-15 lg:w-15'
        href='tel:1900272737'
      >
        <Link
          href='tel:1900272737'
          className='invisible absolute right-16 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1 rounded-md bg-light p-1.5 font-bold text-primary opacity-0 shadow-[0px_3px_8px_0px_#00000033] transition-all duration-300 group-hover:visible group-hover:translate-x-0 group-hover:opacity-100'
        >
          <svg
            width='12'
            height='12'
            viewBox='0 0 23 23'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M22.374 16.8797L19.1643 13.67C18.018 12.5236 16.0692 12.9822 15.6107 14.4724C15.2668 15.5041 14.1205 16.0773 13.0888 15.848C10.7961 15.2748 7.70102 12.2944 7.12786 9.88709C6.78396 8.85535 7.47176 7.70902 8.50345 7.36517C9.99368 6.90663 10.4522 4.95788 9.30588 3.81155L6.09616 0.60183C5.1791 -0.2006 3.80351 -0.2006 3.00108 0.60183L0.823054 2.77985C-1.35497 5.07251 1.05232 11.148 6.44006 16.5358C11.8278 21.9235 17.9033 24.4455 20.196 22.1528L22.374 19.9748C23.1765 19.0577 23.1765 17.6821 22.374 16.8797Z'
              fill='#2F5ACF'
            />
          </svg>
          1900.272737
        </Link>
      </ContactButton>

      <ContactButton
        icon=''
        alt='close'
        width={0}
        height={0}
        className=''
        onClick={toggleExpand}
        href=''
        additionalClasses='flex h-10 w-10 items-center justify-center rounded-full border border-neutral-600/20 bg-neutral-600/10 lg:h-15 lg:w-15'
      >
        <X className='h-4 w-4 lg:h-5 lg:w-5' />
      </ContactButton>

      <style jsx>{`
        .expanded-buttons {
          opacity: 0;
          transform: scale(0.9);
          animation: expandIn 0.12s ease forwards;
        }

        @keyframes expandIn {
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  )

  return (
    <div className='fixed bottom-16 right-4 z-40' id='contact-fab'>
      {!isExpand ? renderMainButton() : renderExpandedButtons()}
    </div>
  )
}
